'use client'

import RichTextEditor from '@/components/rich-text-editor'
import {
    StoreWidget,
    createWidget,
    getWidget,
    updateWidget,
} from '@/lib/api/commerce/store-widgets'
import { useAuth } from '@/store/hooks'
import Editor from '@monaco-editor/react'
import { useEffect, useState } from 'react'
import { MdArrowBack, MdSave, MdWidgets } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

type WidgetType = 'HTML' | 'HTML_CODE' | 'JAVASCRIPT'

export default function WidgetFormPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const { user } = useAuth()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    const [name, setName] = useState('')
    const [type, setType] = useState<WidgetType>('HTML')
    const [content, setContent] = useState('')
    const [active, setActive] = useState(true)

    useEffect(() => {
        if (isEditing) {
            loadWidget()
        }
    }, [id])

    const loadWidget = async () => {
        setLoading(true)
        try {
            const res = await getWidget(parseInt(id!, 10))
            if (res.success && res.result) {
                const widget = res.result
                setName(widget.name)
                if (widget.type === 'HTML' && (widget.content.includes('<script') || widget.content.includes('<style'))) {
                    setType('HTML_CODE')
                } else {
                    setType(widget.type)
                }
                setContent(widget.content)
                setActive(widget.active)
            } else {
                toast.error('Widget não encontrado')
                navigate('/commerce/stores/widgets')
            }
        } catch {
            toast.error('Erro ao carregar widget')
            navigate('/commerce/stores/widgets')
        } finally {
            setLoading(false)
        }
    }

    const handleSave = async () => {
        const companyId = user?.company?.id
        if (!companyId) {
            toast.error('Empresa não encontrada')
            return
        }

        if (!name.trim()) {
            toast.error('Nome do widget é obrigatório')
            return
        }

        if (!content.trim()) {
            toast.error('Conteúdo do widget é obrigatório')
            return
        }

        setSaving(true)
        try {
            const data: Partial<StoreWidget> = {
                companyId,
                name: name.trim(),
                type: type === 'HTML_CODE' ? 'HTML' : type,
                content,
                active,
            }

            let res
            if (isEditing) {
                res = await updateWidget(parseInt(id!, 10), data)
            } else {
                res = await createWidget(data)
            }

            if (res.success) {
                toast.success(
                    isEditing
                        ? 'Widget atualizado com sucesso'
                        : 'Widget criado com sucesso',
                )
                navigate('/commerce/stores/widgets')
            } else {
                toast.error(
                    (res as any).message || 'Erro ao salvar widget',
                )
            }
        } catch {
            toast.error('Erro ao salvar widget')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <span className="loading loading-spinner loading-lg" />
            </div>
        )
    }

    return (
        <div className="mx-auto max-w-5xl space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate('/commerce/stores/widgets')}
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <MdWidgets className="text-secondary text-3xl" />
                    <div>
                        <h1 className="text-2xl font-bold">
                            {isEditing ? 'Editar Widget' : 'Novo Widget'}
                        </h1>
                        <p className="text-base-content/60 text-sm">
                            {isEditing
                                ? 'Modifique o conteúdo do widget'
                                : 'Crie um novo widget para sua loja'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <label className="label cursor-pointer gap-2">
                        <span className="label-text font-medium">
                            {active ? 'Ativo' : 'Inativo'}
                        </span>
                        <input
                            type="checkbox"
                            className="toggle toggle-secondary"
                            checked={active}
                            onChange={(e) => setActive(e.target.checked)}
                        />
                    </label>
                    <button
                        className="btn btn-secondary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdSave className="text-lg" />
                        )}
                        Salvar
                    </button>
                </div>
            </div>

            {/* Nome e Tipo */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome do Widget *
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex: Banner Promocional"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Tipo *
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={type}
                                onChange={(e) => {
                                    const newType = e.target.value as WidgetType
                                    setType(newType)
                                    if (!isEditing) {
                                        setContent('')
                                    }
                                }}
                            >
                                <option value="HTML">
                                    Visual (Editor de texto)
                                </option>
                                <option value="HTML_CODE">
                                    HTML (Código com scripts)
                                </option>
                                <option value="JAVASCRIPT">
                                    JavaScript (Iframe isolado)
                                </option>
                            </select>
                            <label className="label">
                                <span className="label-text-alt text-base-content/50">
                                    {type === 'HTML'
                                        ? 'Editor visual para textos, imagens, vídeos e formatação'
                                        : type === 'HTML_CODE'
                                        ? 'Código HTML com suporte a scripts e estilos, renderizado diretamente na página'
                                        : 'Editor de código para scripts JavaScript, executado em iframe isolado'}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* Editor */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg">
                        Conteúdo do Widget *
                    </h2>

                    {type === 'HTML' ? (
                        <RichTextEditor
                            content={content}
                            onChange={setContent}
                            placeholder="Digite o conteúdo do widget..."
                            minHeight="400px"
                        />
                    ) : (
                        <div className="overflow-hidden rounded-lg border">
                            <Editor
                                key={`editor-${type}`}
                                height="400px"
                                language={type === 'HTML_CODE' ? 'html' : 'javascript'}
                                value={content}
                                onChange={(value) =>
                                    setContent(value || '')
                                }
                                theme="vs-dark"
                                options={{
                                    minimap: { enabled: false },
                                    fontSize: 14,
                                    lineNumbers: 'on',
                                    scrollBeyondLastLine: false,
                                    wordWrap: 'on',
                                    automaticLayout: true,
                                    tabSize: 2,
                                    padding: { top: 12 },
                                }}
                            />
                        </div>
                    )}

                    {type === 'JAVASCRIPT' && (
                        <div className="alert alert-warning mt-2">
                            <span className="text-sm">
                                O código JavaScript será executado em um iframe
                                isolado (sandbox) na loja. Não terá acesso ao
                                DOM principal da página. Para embeds de
                                Instagram, YouTube, TikTok e afins (código que
                                começa com &lt;blockquote&gt;, &lt;iframe&gt; ou
                                &lt;div&gt;), use o tipo HTML (Código com
                                scripts).
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
